import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { AdminNavigation } from "../components/navigation/AdminNavigation";
import { toast } from "sonner";
import {
  ArrowLeft,
  Mic2,
  Search,
  ShieldCheck,
  ShieldAlert,
  Globe,
  DollarSign,
  Loader2,
  ChevronRight,
  Eye,
  EyeOff,
  Trash2,
  AlertTriangle,
} from "lucide-react";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /admin/artists — Bank-grade admin-only directory of every merchant/artist.
 * Filter by country, verification status, and free-text search. Clicking a
 * row loads full profile + events + products + experiences. Non-admins are
 * blocked at the API layer.
 */
const AdminArtistsDirectory = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [country, setCountry] = useState("");
  const [verifiedFilter, setVerifiedFilter] = useState("all"); // all | verified | pending
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (country) params.set("country", country);
      if (search) params.set("search", search);
      if (verifiedFilter === "verified") params.set("verified", "true");
      else if (verifiedFilter === "pending") params.set("verified", "false");
      const { data } = await axios.get(`${API}/admin/artists/list?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(data.artists || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load artists");
    } finally {
      setLoading(false);
    }
  }, [token, search, country, verifiedFilter]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (row) => {
    setSelected(row);
    setDetail(null);
    try {
      const { data } = await axios.get(`${API}/admin/artists/${row.artist_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDetail(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load artist");
    }
  };

  /* -------- Hide / Unhide / Delete row actions -------- */
  const [busyUserId, setBusyUserId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // row being deleted
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  const handleHide = async (row) => {
    const uid = row.user_id;
    if (!uid) { toast.error("Missing user_id — cannot hide"); return; }
    setBusyUserId(uid);
    try {
      await axios.post(
        `${API}/admin/users/${uid}/hide`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`${row.stage_name || row.legal_name || "Account"} hidden`);
      setRows((prev) => prev.map((r) => (r.user_id === uid ? { ...r, is_hidden: true } : r)));
    } catch (e) {
      if (e.response?.status === 428) {
        toast.error("Enter your Owner Passport ID to unlock this action.");
      } else {
        toast.error(e.response?.data?.detail || "Failed to hide account");
      }
    } finally {
      setBusyUserId(null);
    }
  };

  const handleUnhide = async (row) => {
    const uid = row.user_id;
    if (!uid) { toast.error("Missing user_id — cannot unhide"); return; }
    setBusyUserId(uid);
    try {
      await axios.post(
        `${API}/admin/users/${uid}/unhide`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`${row.stage_name || row.legal_name || "Account"} unhidden`);
      setRows((prev) => prev.map((r) => (r.user_id === uid ? { ...r, is_hidden: false } : r)));
    } catch (e) {
      if (e.response?.status === 428) {
        toast.error("Enter your Owner Passport ID to unlock this action.");
      } else {
        toast.error(e.response?.data?.detail || "Failed to unhide account");
      }
    } finally {
      setBusyUserId(null);
    }
  };

  const openDeleteDialog = (row) => {
    if (!row.user_id) { toast.error("Missing user_id — cannot delete"); return; }
    setDeleteTarget(row);
    setDeleteConfirmText("");
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const uid = deleteTarget.user_id;
    const expectedEmail = (deleteTarget.email || "").trim().toLowerCase();
    const typedEmail = deleteConfirmText.trim().toLowerCase();
    if (!expectedEmail) {
      toast.error("This account has no email on file — cannot verify.");
      return;
    }
    if (typedEmail !== expectedEmail) {
      toast.error(`Type the account's exact email (${expectedEmail}) to confirm.`);
      return;
    }
    setBusyUserId(uid);
    try {
      await axios.delete(
        `${API}/admin/users/${uid}?email_confirm=${encodeURIComponent(expectedEmail)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`${deleteTarget.stage_name || deleteTarget.legal_name || expectedEmail} permanently deleted`);
      setRows((prev) => prev.filter((r) => r.user_id !== uid));
      if (selected?.user_id === uid) { setSelected(null); setDetail(null); }
      setDeleteTarget(null);
    } catch (e) {
      if (e.response?.status === 428) {
        toast.error("Enter your Owner Passport ID to unlock this action.");
      } else if (e.response?.status === 400) {
        toast.error(e.response?.data?.detail || "Email did not match.");
      } else {
        toast.error(e.response?.data?.detail || "Failed to delete account");
      }
    } finally {
      setBusyUserId(null);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-black text-white pb-24" data-testid="admin-artists-page">
      <header className="sticky top-0 z-30 bg-black/85 backdrop-blur border-b border-[#1A2030]">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-lg border border-[#1A2030] hover:bg-[#1A2030] flex items-center justify-center" data-testid="artists-dir-back-btn" aria-label="Back">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <Link to="/home" className="flex items-center gap-2">
            <img src="/logo.png" alt="StardomFans" className="w-10 h-10 object-contain" style={{ mixBlendMode: "lighten" }} />
            <span className="hidden sm:inline text-white font-bold">Admin · Merchants / Artists</span>
          </Link>
          <div className="ml-auto flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#F0C464]" />
            <span className="text-sm text-[#8A92A0]" data-testid="artists-dir-count">{rows.length} accounts</span>
          </div>
        </div>
      </header>
      <AdminNavigation />

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="font-['Unbounded'] text-3xl font-bold text-white mb-2">Merchants / Artists directory</h1>
          <p className="text-[#8A92A0] max-w-3xl">
            Bank-grade admin-only list of every merchant. Profile, verification state, confirmed billing address, and lifetime revenue for each artist. 5/5 leak-proof — non-admin roles are blocked at the API layer.
          </p>
        </div>

        <div className="card p-4 mb-6 flex flex-wrap items-center gap-3" data-testid="artists-dir-filter-bar">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A92A0]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search stage name, legal name, artist_id…"
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-[#050810] border border-[#1A2030] text-white text-sm"
              data-testid="artists-dir-search-input"
            />
          </div>
          <input
            type="text"
            value={country}
            onChange={(e) => setCountry(e.target.value.toUpperCase())}
            placeholder="Country (US, CA, GB…)"
            maxLength={2}
            className="w-32 px-3 py-2 rounded-lg bg-[#050810] border border-[#1A2030] text-white text-sm uppercase font-mono"
            data-testid="artists-dir-country-input"
          />
          <div className="flex items-center gap-1">
            {[
              { key: "all", label: "All" },
              { key: "verified", label: "Verified" },
              { key: "pending", label: "Pending KYC" },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setVerifiedFilter(f.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  verifiedFilter === f.key
                    ? "bg-[#F0C464] text-black"
                    : "bg-[#0A0E18] border border-[#1A2030] text-[#8A92A0] hover:text-white"
                }`}
                data-testid={`artists-dir-filter-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#F0C464]" /></div>
        ) : rows.length === 0 ? (
          <div className="card p-16 text-center" data-testid="artists-dir-empty">
            <Mic2 className="w-12 h-12 text-[#8A92A0] mx-auto mb-4" />
            <h3 className="text-white font-semibold mb-2">No artists match your filters</h3>
          </div>
        ) : (
          <div className="card overflow-hidden" data-testid="artists-dir-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#1A2030] text-left text-[10px] uppercase tracking-widest text-[#8A92A0]">
                    <th className="px-4 py-3">Stage / Legal</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Country</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">Lifetime revenue</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((a) => (
                    <tr key={a.artist_id} className={`border-b border-[#1A2030]/50 hover:bg-[#0A0E18] ${a.is_hidden ? "opacity-60" : ""}`} data-testid={`artist-dir-row-${a.artist_id}`}>
                      <td className="px-4 py-3">
                        <p className="text-white font-semibold">{a.stage_name || a.legal_name || a.artist_id}</p>
                        <p className="text-xs text-[#8A92A0]">{a.legal_name}</p>
                      </td>
                      <td className="px-4 py-3 text-xs text-[#8A92A0] font-mono">{a.email || "—"}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 text-xs text-[#8A92A0]">
                          <Globe className="w-3 h-3" /> {a.country || (a.address || {}).country || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          {a.is_verified ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#8DB87A]/20 text-[#8DB87A] text-[10px] font-bold uppercase tracking-widest w-fit">
                              <ShieldCheck className="w-3 h-3" /> Verified
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#EBB84C]/20 text-[#EBB84C] text-[10px] font-bold uppercase tracking-widest w-fit">
                              <ShieldAlert className="w-3 h-3" /> Pending
                            </span>
                          )}
                          {a.is_hidden && (
                            <span
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#EC008C]/20 text-[#EC008C] text-[10px] font-bold uppercase tracking-widest w-fit"
                              data-testid={`artist-dir-hidden-badge-${a.artist_id}`}
                            >
                              <EyeOff className="w-3 h-3" /> Hidden
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right font-mono text-white">
                        ${(a.lifetime_revenue || 0).toFixed(2)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 justify-end flex-wrap">
                          <button
                            onClick={() => openDetail(a)}
                            className="text-[#F0C464] text-xs font-bold hover:underline inline-flex items-center gap-1"
                            data-testid={`artist-dir-detail-btn-${a.artist_id}`}
                          >
                            Open <ChevronRight className="w-3 h-3" />
                          </button>
                          <div className="w-px h-4 bg-[#1A2030] mx-1" />
                          {a.is_hidden ? (
                            <button
                              onClick={() => handleUnhide(a)}
                              disabled={busyUserId === a.user_id}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded bg-[#0A0E18] border border-[#1A2030] hover:border-[#8DB87A]/50 text-[#8DB87A] text-[10px] font-bold uppercase tracking-widest disabled:opacity-40"
                              data-testid={`artist-dir-unhide-btn-${a.artist_id}`}
                              title="Restore this account to public listings"
                            >
                              <Eye className="w-3 h-3" /> Unhide
                            </button>
                          ) : (
                            <button
                              onClick={() => handleHide(a)}
                              disabled={busyUserId === a.user_id}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded bg-[#0A0E18] border border-[#1A2030] hover:border-[#F0C464]/50 text-[#F0C464] text-[10px] font-bold uppercase tracking-widest disabled:opacity-40"
                              data-testid={`artist-dir-hide-btn-${a.artist_id}`}
                              title="Soft-hide this account from public listings (reversible)"
                            >
                              <EyeOff className="w-3 h-3" /> Hide
                            </button>
                          )}
                          <button
                            onClick={() => openDeleteDialog(a)}
                            disabled={busyUserId === a.user_id}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded bg-[#0A0E18] border border-[#1A2030] hover:border-[#EC008C]/50 text-[#EC008C] text-[10px] font-bold uppercase tracking-widest disabled:opacity-40"
                            data-testid={`artist-dir-delete-btn-${a.artist_id}`}
                            title="Permanent cascade delete — cannot be undone"
                          >
                            <Trash2 className="w-3 h-3" /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur flex items-center justify-center p-4" data-testid="artist-dir-detail-modal" onClick={() => { setSelected(null); setDetail(null); }}>          <div className="bg-[#0A0E18] border border-[#1A2030] rounded-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-xs text-[#8A92A0] font-mono mb-1">{selected.artist_id}</p>
                <h2 className="font-['Unbounded'] text-2xl font-bold text-white">{selected.stage_name || selected.legal_name}</h2>
                <p className="text-sm text-[#8A92A0]">{selected.email || detail?.user?.email}</p>
              </div>
              <button onClick={() => { setSelected(null); setDetail(null); }} className="text-[#8A92A0] hover:text-white text-xl leading-none">✕</button>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="rounded-lg bg-[#050810] border border-[#1A2030] p-3">
                <p className="text-[10px] uppercase tracking-widest text-[#8A92A0]">Genre</p>
                <p className="text-white font-bold">{selected.genre || "—"}</p>
              </div>
              <div className="rounded-lg bg-[#050810] border border-[#1A2030] p-3">
                <p className="text-[10px] uppercase tracking-widest text-[#8A92A0]">Lifetime revenue</p>
                <p className="text-white font-bold text-lg flex items-center gap-1"><DollarSign className="w-4 h-4 text-[#8DB87A]" /> ${(selected.lifetime_revenue || 0).toFixed(2)}</p>
              </div>
            </div>

            {detail && (
              <>
                <div className="rounded-lg bg-[#050810] border border-[#1A2030] p-3 mb-4">
                  <p className="text-[10px] uppercase tracking-widest text-[#8A92A0] mb-2">Confirmed billing address</p>
                  {selected.address && Object.keys(selected.address).length ? (
                    <div className="text-sm text-white">
                      <p>{selected.address.address_line1 || "—"} {selected.address.address_line2 || ""}</p>
                      <p>{selected.address.city || "—"}, {selected.address.state || "—"} {selected.address.postal_code || ""}</p>
                      <p className="text-[#8A92A0]">{selected.address.country || "—"}</p>
                    </div>
                  ) : (
                    <p className="text-xs text-[#8A92A0]">No address on file.</p>
                  )}
                </div>

                <h3 className="text-white font-bold mb-2">Events ({detail.events?.length || 0})</h3>
                <div className="space-y-1 mb-3 max-h-32 overflow-y-auto">
                  {(detail.events || []).slice(0, 20).map((e) => (
                    <div key={e.event_id} className="rounded bg-[#050810] border border-[#1A2030] p-2 text-xs flex items-center justify-between">
                      <span className="text-white">{e.title}</span>
                      <span className="text-[#8A92A0]">${e.ticket_price} · sold {e.tickets_sold || 0}</span>
                    </div>
                  ))}
                </div>

                <h3 className="text-white font-bold mb-2">Products ({detail.products?.length || 0})</h3>
                <div className="space-y-1 mb-3 max-h-32 overflow-y-auto">
                  {(detail.products || []).slice(0, 20).map((p) => (
                    <div key={p.product_id} className="rounded bg-[#050810] border border-[#1A2030] p-2 text-xs flex items-center justify-between">
                      <span className="text-white">{p.name}</span>
                      <span className="text-[#8A92A0]">${p.price} · {p.category}</span>
                    </div>
                  ))}
                </div>

                <h3 className="text-white font-bold mb-2">1:1 Experiences ({detail.experiences?.length || 0})</h3>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {(detail.experiences || []).slice(0, 20).map((x) => (
                    <div key={x.experience_id} className="rounded bg-[#050810] border border-[#1A2030] p-2 text-xs flex items-center justify-between">
                      <span className="text-white">{x.title}</span>
                      <span className="text-[#8A92A0]">${x.price} · {x.duration_minutes}min</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Delete confirmation dialog — permanent cascade */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent
          className="bg-[#0A0E18] border border-[#EC008C]/50 text-white max-w-md"
          data-testid="admin-delete-dialog"
        >
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-[#EC008C]/15 text-[#EC008C] flex items-center justify-center">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <DialogTitle className="text-white font-['Unbounded']">Permanent cascade delete</DialogTitle>
            </div>
            <DialogDescription className="text-[#8A92A0]">
              You are about to permanently delete{" "}
              <span className="text-white font-semibold">
                {deleteTarget?.stage_name || deleteTarget?.legal_name || deleteTarget?.email}
              </span>{" "}
              and remove their sessions, products, storefront, posts and identity documents.
              Accounts with financial history cannot be deleted; hide them instead. This cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div>
              <label className="text-xs text-[#8A92A0] uppercase tracking-widest">
                Type <span className="text-[#EC008C] font-mono">
                  {deleteTarget?.email}
                </span> to confirm
              </label>
              <Input
                type="email"
                autoComplete="off"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                className="mt-1 bg-[#050810] border-[#1A2030] text-white font-mono"
                data-testid="admin-delete-confirm-input"
                placeholder="Exact account email"
              />
              <p className="text-[10px] text-[#4A5260] mt-1">
                Financial and tax records are protected from deletion.
                Owner Passport must be unlocked. This cannot be undone.
              </p>
            </div>
          </div>

          <DialogFooter className="flex gap-2 justify-end mt-2">
            <Button
              variant="ghost"
              onClick={() => setDeleteTarget(null)}
              className="text-[#8A92A0] hover:text-white"
              data-testid="admin-delete-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmDelete}
              disabled={busyUserId === deleteTarget?.user_id}
              className="bg-[#EC008C] text-black font-bold hover:bg-[#FF3399]"
              data-testid="admin-delete-confirm"
            >
              {busyUserId === deleteTarget?.user_id ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Deleting…</>
              ) : (
                <><Trash2 className="w-4 h-4 mr-2" /> Permanently delete</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminArtistsDirectory;
